import { useState, useEffect, useCallback, useRef } from 'react';
import { formatFranc } from '../lib/utils';
import { entityAPI, teamAPI } from '../lib/api';
import { nextPenaltyRate, freeFillsLeft } from '../lib/rosterMoveMath';
import FeeWarning from '../components/FeeWarning';
import DropPreview from '../components/DropPreview';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Virtuoso } from 'react-virtuoso';
import BackButton from '../components/BackButton';
import EntityAvatar from '../components/EntityAvatar';
import ConfirmDialog from '../components/ConfirmDialog';
import DraftingOverlay from '../components/DraftingOverlay';
import StarterPicksRow from '../components/StarterPicksRow';
import { Search, Plus, X, Users, Filter, ChevronDown, Dices } from 'lucide-react';
import FrancIcon from '../components/FrancIcon';
import { toast } from 'sonner';
import { posthogCapture } from '../posthog.config';
import { SharedScoreboardsNote } from '../components/SharedScoreboardsNote';
import { LeagueClosedNote } from '../components/LeagueClosedNote';

const PAGE_SIZE = 50;

// v5.7.59 — Canonical 6-category list. `fictional` was retired in
// v5.7.23 but the enum + some legacy league rows still list it; we
// hard-filter it out at render time so the totals math (max roster
// 18) never reverts to 21.
const ENTITY_TYPES = [
  { value: '', label: 'All' },
  { value: 'country_leader', label: 'Rulers' },
  { value: 'ceo', label: 'CEOs' },
  { value: 'actor', label: 'Actors' },
  { value: 'athlete', label: 'Athletes' },
  { value: 'musician', label: 'Artists' },
  { value: 'influencer', label: 'Influencers' },
];

// Display-only mapping for per-row category Badge. Mirrors the same
// map in ExploreTab.js so Draft Room and Search show identical labels.
const ENTITY_TYPE_SINGULAR = {
  country_leader: 'Ruler',
  ceo: 'CEO',
  actor: 'Actor',
  athlete: 'Athlete',
  musician: 'Artist',
  influencer: 'Influencer',
};

// Same colour palette as ExploreTab → consistent ovals across the app.
const ENTITY_TYPE_COLORS = {
  country_leader: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  ceo:            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  actor:          'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  athlete:        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  musician:       'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  influencer:     'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
};

// Canonical-only list used to filter out legacy values like 'fictional'
const VALID_CATEGORY_VALUES = new Set(ENTITY_TYPES.slice(1).map(t => t.value));

// iter_182 — compute the initial category chip BEFORE the first fetch so a
// restricted league never fires an all-category request that can win the
// race against the snapped-category one (stale-list flash).
const initialCategoryFor = (leagueSettings) => {
  const sc = leagueSettings?.slot_config || {};
  const hasSC = Object.keys(sc).length > 0;
  const allowed = new Set((hasSC
    ? Object.entries(sc).filter(([k, v]) => v > 0).map(([k]) => k)
    : (leagueSettings?.allowed_categories || Array.from(VALID_CATEGORY_VALUES))
  ).filter((c) => VALID_CATEGORY_VALUES.has(c)));
  if (allowed.size === 0 || allowed.size >= VALID_CATEGORY_VALUES.size) return '';
  return ENTITY_TYPES.slice(1).find((t) => allowed.has(t.value))?.value || '';
};

// 2026-07-20 launch perf — react-virtuoso Footer: Load More + bottom
// clearance (pb-24 keeps the last rows above any overlay).
const DraftListFooter = ({ context }) => (
  <div className="pb-24 pt-1.5">
    {context?.hasMore ? (
      <Button
        variant="outline"
        className="w-full"
        onClick={context.loadMore}
        disabled={context.loadingMore}
        data-testid="draft-load-more-button"
      >
        {context.loadingMore ? 'Loading…' : (
          <><ChevronDown className="h-4 w-4 mr-1" /> Load more ({context.shown} shown)</>
        )}
      </Button>
    ) : null}
  </div>
);
const DRAFT_VIRTUOSO_COMPONENTS = { Footer: DraftListFooter };

export default function DraftRoom({ onBack, teamId, leagueId, poolSize, leagueSettings, leagueName, onTeamUpdate, onEntityClick }) {
  const [entities, setEntities] = useState([]);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [category, setCategory] = useState(() => initialCategoryFor(leagueSettings));
  const [sortBy, setSortBy] = useState('pricing.price');
  const [sortDir, setSortDir] = useState(-1);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftingId, setDraftingId] = useState(null);
  // Synchronous double-submit guard. React state (setDraftingId) is batched,
  // so two rapid taps in the same render tick both observe the stale
  // draftingId and both POST /teams/draft — the first wins (200), the rest
  // hit the server's "already drafted"/slot-conflict path (400). A ref flips
  // synchronously and survives the batching window. (Sentry b3210f7b.)
  const draftInFlightRef = useRef(false);
  const [removingId, setRemovingId] = useState(null);
  // v5.7.99 — Pagination state. `skip` is the offset, `hasMore` flips
  // false when the backend returns fewer than PAGE_SIZE rows. The
  // "Load More" button appends to `entities` so the user keeps the
  // already-loaded list while scrolling further down.
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // v5.7.99 — Post-finalization Add confirmation. The Drop button
  // already routes through ConfirmDialog elsewhere because dropping
  // costs the user a roster move + escalating penalty; Add deserves
  // the same guard because a misclick lands a person they then
  // have to drop (paying the penalty) to undo. Pre-finalization
  // (during the draft phase) is free both ways → no confirmation.
  const [confirmAdd, setConfirmAdd] = useState(null);  // { entityId, name, price }
  const [confirmDrop, setConfirmDrop] = useState(null); // { entityId, name }

  // iter_182 — sequence guard: a newer filter fetch invalidates any in-flight
  // older one (loadData had no stale-response protection; the mount-time
  // all-category fetch could overwrite the snapped-category result).
  const loadSeqRef = useRef(0);
  // 2026-08-31 (owner request) — one-tap smart-random roster fill.
  const [showRandomConfirm, setShowRandomConfirm] = useState(false);
  const [randomBusy, setRandomBusy] = useState(false);
  const loadData = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setSkip(0);
    try {
      const params = { limit: PAGE_SIZE, skip: 0, sort_by: sortBy, sort_dir: sortDir };
      if (searchDebounced) params.search = searchDebounced;
      if (category) params.entity_type = category;
      // 2026-08-15 — scope the draftable list to the league's people pool
      // (allowed_entity_ids mask); no-op for unmasked leagues.
      if (leagueId) { params.league_id = leagueId; params.pool_only = true; }
      const [entRes, tmRes] = await Promise.all([
        entityAPI.list(params),
        teamAPI.myTeams()
      ]);
      if (seq !== loadSeqRef.current) return;
      const fetched = entRes.data.entities || [];
      setEntities(fetched);
      setHasMore(fetched.length === PAGE_SIZE);
      if (tmRes.data.teams?.length > 0) {
        // 2026-06 league audit — STRICT resolution: nav teamId first, then the
        // header-selected league. Blind teams[0] survives ONLY when neither is
        // known (legacy deep-link), never alongside an explicit selection.
        const list = tmRes.data.teams;
        const t = (teamId && list.find(x => x.id === teamId))
          || (leagueId && list.find(x => x.league_id === leagueId))
          || ((!teamId && !leagueId) ? list[0] : null)
          || null;
        setTeam(t);
      }
    } catch (e) { console.error(e); }
    finally { if (seq === loadSeqRef.current) setLoading(false); }
  }, [searchDebounced, category, teamId, sortBy, sortDir, leagueId]);

  // v5.7.99 — Load next page. Appends to existing `entities` and
  // advances `skip` by PAGE_SIZE. If the response returns fewer than
  // PAGE_SIZE rows we know we've hit the end.
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const seq = loadSeqRef.current;
    try {
      const nextSkip = skip + PAGE_SIZE;
      const params = { limit: PAGE_SIZE, skip: nextSkip, sort_by: sortBy, sort_dir: sortDir };
      if (searchDebounced) params.search = searchDebounced;
      if (category) params.entity_type = category;
      if (leagueId) { params.league_id = leagueId; params.pool_only = true; }
      const res = await entityAPI.list(params);
      if (seq !== loadSeqRef.current) return;
      const fetched = res.data.entities || [];
      // De-dupe on append — see ExploreTab loadMore.
      setEntities(prev => {
        const seen = new Set(prev.map(x => x.id));
        return [...prev, ...fetched.filter(x => x && !seen.has(x.id))];
      });
      setSkip(nextSkip);
      setHasMore(fetched.length === PAGE_SIZE);
    } catch (e) {
      console.error('[DraftRoom] loadMore failed:', e);
      toast.error('Failed to load more — try again');
    } finally {
      setLoadingMore(false);
    }
  };

  // PERF (2026-07 final audit P1 — Sonnet 4.6 + Gemini 3.1 Pro): debounce the
  // search input by 350ms, mirroring ExploreTab. Previously every keystroke
  // fired a GET /entities + GET /teams/my pair (N×2 calls per term + out-of-
  // order race where a slow earlier response overwrote a faster later one).
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  // v5.7.99 — Wrapper that confirms before adding when the team is
  // post-finalization. During the open draft phase (`!team.finalized`)
  // adds are free and reversible, so we skip the dialog. Post-
  // finalization, an accidental add lands a person you then have to
  // drop (incurring a roster-move penalty) to undo — same cost
  // profile as the existing Drop confirmation, so it gets the same
  // guard.
  const handleAddClick = (entity) => {
    if (team?.finalized) {
      setConfirmAdd({
        entityId: entity.id,
        name: entity.display_name || entity.name,
        price: entity.pricing?.price,
      });
      return;
    }
    handleDraft(entity.id, entity.display_name || entity.name);
  };

  // v5.7.100 — Symmetric with handleAddClick: confirm ONLY when
  // post-finalized. Pre-finalize drops are free + reversible, so
  // they should be one-click like Add. Was always-confirming in
  // v5.7.99 which created annoying friction during the open
  // draft phase.
  const handleRemoveClick = (entityId, name) => {
    if (team?.finalized) {
      setConfirmDrop({ entityId, name });
      return;
    }
    handleRemove(entityId, name);
  };

  const handleDraft = async (entityId, entityName) => {
    if (!team) { toast.error('No team found'); return; }
    
    // Block drafting people from disallowed categories
    const entity = entities.find(e => e.id === entityId);
    if (entity && !allowedCategories.includes(entity.entity_type)) {
      toast.error(`${entity.entity_type.replace(/_/g, ' ')} entities are not allowed in this league`);
      return;
    }
    // Block if category is full
    if (entity && (categoryCounts[entity.entity_type] || 0) >= getMaxForCategory(entity.entity_type)) {
      toast.error(`${entity.entity_type.replace(/_/g, ' ')} slots are full`);
      return;
    }
    // Block if roster is full
    if (slotsUsed >= totalSlots) {
      toast.error('Roster is full');
      return;
    }

    // Synchronous guard — drop concurrent taps before React re-renders the
    // disabled button (prevents the duplicate 400 + unhandled rejection).
    if (draftInFlightRef.current) return;
    draftInFlightRef.current = true;

    setDraftingId(entityId);
    try {
      const res = await teamAPI.draft({
        team_id: team.id,
        entity_id: entityId,
        // Build 4 — cross-league guard: server 409s when the header league
        // and this team's league drifted apart (stale UI).
        league_id: leagueId || team.league_id,
      });
      // iter_182 — surface the roster-move fee + new budget (the fee was
      // being charged silently on post-finalize adds).
      const d = res.data || {};
      let addMsg = d.message || `Added ${entityName}`;
      if (d.roster_move && d.new_budget != null) addMsg += ` — budget ${formatFranc(d.new_budget)}`;
      toast.success(addMsg);
      // Funnel step 2 — draft. Fires on each successful draft add.
      posthogCapture('draft', { category: entity?.entity_type });
      // Refresh canonical team state from server (was relying on
      // optimistic local push, which caused stale-state drift in the
      // drop → draft → drop cycle reported in v5.7.38).
      const tmRes = await teamAPI.myTeams();
      const fresh = tmRes.data.teams?.find(t => t.id === team.id) || null;
      if (fresh) {
        // Milestone — roster just became full ("acquired a team" / draft
        // completed). Fire once on the empty→full transition; PostHog funnels
        // dedupe to first occurrence per user, so you can measure D1 retention
        // by acquisition channel (signup → draft → roster_complete → ad-free).
        const wasFull = (team?.slots?.length || 0) >= totalSlots;
        const nowFull = (fresh.slots?.length || 0) >= totalSlots && totalSlots > 0;
        if (!wasFull && nowFull) {
          posthogCapture('roster_complete', { slots: fresh.slots?.length || 0 });
        }
        setTeam(fresh);
        if (onTeamUpdate) onTeamUpdate(fresh);
      }
    } catch (e) {
      // Surface the server's reason (e.g. roster full / already drafted)
      // as a toast instead of letting the rejection bubble up as an
      // unhandled promise rejection (Sentry noise).
      const detail = e?.response?.data?.detail;
      const reason = typeof detail === 'string' ? detail : detail?.message;
      toast.error(reason || `Couldn't add ${entityName}. Please try again.`);
    } finally {
      draftInFlightRef.current = false;
      setDraftingId(null);
    }
  };

  const handleRemove = async (entityId, entityName) => {
    if (!team) return;
    setRemovingId(entityId);
    try {
      const res = await teamAPI.remove({ team_id: team.id, entity_id: entityId });
      // Compose user-facing message using the actual server-computed
      // refund/penalty so post-finalize drops correctly show the penalty
      // hit. The old optimistic `acquired_price * 0.9` math was a relic
      // of the pre-v5.7.33 economy and silently masked the penalty —
      // user reported this as "drop after finalize avoided the penalty"
      // even though the backend was charging it correctly.
      const { refund = 0, penalty = 0, net_delta = 0, memorial_removal } = res.data || {};
      if (memorial_removal) {
        toast.success(`Memorial removal: ${entityName} (free, full ${formatFranc(refund)} refund)`);
      } else if (penalty > 0) {
        toast.success(
          `Removed ${entityName} · Refund ${formatFranc(refund)} · Penalty ${formatFranc(penalty)}`
        );
      } else {
        toast.success(`Removed ${entityName} · ${formatFranc(refund)} refund`);
      }

      // Re-fetch the canonical team state from the server. The previous
      // implementation faked an optimistic update with the wrong math
      // (0.9 × acquired_price), which left the displayed budget several
      // ₣ ahead of the server's real value — and following operations
      // were computed against that stale local state, compounding the
      // drift across draft → drop → draft → drop cycles.
      const tmRes = await teamAPI.myTeams();
      const fresh = tmRes.data.teams?.find(t => t.id === team.id) || null;
      if (fresh) {
        setTeam(fresh);
        if (onTeamUpdate) onTeamUpdate(fresh);
      } else {
        // Defensive fallback: backend says the team is gone — use the
        // returned net_delta to compose a sensible budget.
        setTeam(prev => prev ? {
          ...prev,
          slots: (prev.slots || []).filter(s => s.entity_id !== entityId),
          budget_remaining: (prev.budget_remaining || 0) + net_delta,
        } : prev);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally { setRemovingId(null); }
  };

  const isOnTeam = (entityId) => team?.slots?.some(s => s.entity_id === entityId);
  const slotsUsed = team?.slots?.length || 0;
  const budgetLeft = team?.budget_remaining || 0;
  const categoryCounts = {};
  (team?.slots || []).forEach(s => { categoryCounts[s.entity_type] = (categoryCounts[s.entity_type] || 0) + 1; });

  // League-specific settings
  const slotConfig = leagueSettings?.slot_config || {};
  const hasCustomConfig = Object.keys(slotConfig).length > 0;
  // v5.7.59 — Hard-filter every category source against the canonical
  // 6-category set. Even if a legacy league row still has `fictional`
  // in `allowed_categories`, we drop it here so the totals stay at
  // 18 max instead of regressing to 21.
  const allowedCategories = (hasCustomConfig
    ? Object.entries(slotConfig).filter(([k, v]) => v > 0 && VALID_CATEGORY_VALUES.has(k)).map(([k]) => k)
    : (leagueSettings?.allowed_categories || ENTITY_TYPES.slice(1).map(t => t.value))
  ).filter(c => VALID_CATEGORY_VALUES.has(c));
  const totalSlots = hasCustomConfig
    ? Object.entries(slotConfig)
        .filter(([k, v]) => v > 0 && VALID_CATEGORY_VALUES.has(k))
        .reduce((a, [, v]) => a + v, 0)
    : (allowedCategories.length * (leagueSettings?.required_per_category || 3));
  // The sidebar "Your Roster" header shows the same `totalSlots` cap as
  // the top-of-page chip so the two never disagree. Used to be a
  // hard-coded `/21` which made a 6-category default league look like
  // "18/21" instead of "18/18".
  const rosterCap = totalSlots;
  
  // Per-category max from slot_config or default
  const getMaxForCategory = (cat) => {
    if (hasCustomConfig) return slotConfig[cat] || 0;
    return leagueSettings?.required_per_category || 3;
  };
  
  // Filter people types to only allowed categories with slots > 0
  const allowedChips = ENTITY_TYPES.slice(1).filter(t => allowedCategories.includes(t.value) && getMaxForCategory(t.value) > 0);
  // 2026-08-16 (iter_181) — in a category-restricted league the "All" list is
  // dominated by undraftable people (rows with no Add control), so hide the
  // "All" chip and default to the first allowed category instead.
  const isRestrictedLeague = allowedChips.length > 0 && allowedChips.length < ENTITY_TYPES.slice(1).length;
  const visibleTypes = [...(isRestrictedLeague ? [] : [{ value: '', label: 'All' }]), ...allowedChips];

  // 2026-08-14 — if the selected category filter is disabled in this
  // league's custom config, snap back ("All", or first allowed when restricted).
  useEffect(() => {
    const fallback = isRestrictedLeague ? (allowedChips[0]?.value || '') : '';
    if (category && hasCustomConfig && !(slotConfig[category] > 0)) setCategory(fallback);
    else if (!category && fallback) setCategory(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, hasCustomConfig, leagueSettings]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="draft-room">
      {/* Header */}
      <div className="sticky top-0 z-40 glass-bg border-b border-border/60">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <BackButton onClick={onBack} />
            <h1 className="text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {leagueName || 'Build Your Team'}
            </h1>
            <div className="text-right">
              <Badge variant="secondary" className="tabular-nums text-xs">{formatFranc(budgetLeft)}</Badge>
            </div>
          </div>

          {/* Roster status — flex-wrap (iter_187): 6 category chips + badges
              overflowed horizontally on ≤390px phones, clipping list rows. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs mb-2">
            <Badge className={slotsUsed >= totalSlots ? 'bg-green-500/15 text-green-700' : ''} data-testid="draft-roster-count">
              {slotsUsed}/{totalSlots} drafted
            </Badge>
            {/* 2026-10-12 — finalized with open slots: those fill fee-free */}
            {freeFillsLeft(team) > 0 && (
              <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-700 dark:text-emerald-300" data-testid="draft-free-fills-badge">
                {freeFillsLeft(team)} free fill{freeFillsLeft(team) === 1 ? '' : 's'} left — no move fee
              </Badge>
            )}
            {team && !team.finalized && slotsUsed > 0 && slotsUsed < totalSlots && (
              <Badge variant="outline" className="text-[9px] border-[hsl(var(--accent)/0.4)] text-[hsl(var(--accent))]" data-testid="draft-go-live-hint">
                You can Finalize now — fill the rest later, free
              </Badge>
            )}
            {/* 2026-08-15 — masked-league awareness: live people-pool size
                (pool_size is computed server-side from allowed_entity_ids,
                so editing the pool changes N on the next league fetch). */}
            {poolSize > 0 && (
              <Badge variant="outline" className="text-[9px] border-sky-500/40 text-sky-600 dark:text-sky-300" data-testid="draft-pool-badge">
                Custom pool: {poolSize} people
              </Badge>
            )}
            {allowedChips.map(t => {
              const count = categoryCounts[t.value] || 0;
              const max = getMaxForCategory(t.value);
              if (max <= 0) return null; // Hide categories with 0 slots
              const full = count >= max;
              return (
                <span key={t.value} className={`text-[9px] ${full ? 'text-green-600 font-bold' : 'text-muted-foreground'}`} data-testid={`draft-cat-count-${t.value}`}>
                  {t.label.slice(0, 3)}:{count}/{max}
                </span>
              );
            }).filter(Boolean)}
          </div>

          <SharedScoreboardsNote team={team} testid="draft-shared-scoreboards-note" className="mb-2" />
          <LeagueClosedNote team={team} testid="draft-league-closed-note" className="mb-2" />

          {/* 2026-08-31 — one-tap smart-random roster fill (pre-finalize only) */}
          {!team?.frozen && !team?.finalized && slotsUsed < totalSlots && (
            <Button
              size="sm"
              variant="outline"
              className="w-full mb-2 border-dashed"
              onClick={() => setShowRandomConfirm(true)}
              disabled={randomBusy}
              data-testid="draft-random-btn"
            >
              <Dices className="h-4 w-4 mr-2" />
              {randomBusy ? 'Drafting…' : `Draft Random — fill ${totalSlots - slotsUsed} slot${totalSlots - slotsUsed === 1 ? '' : 's'}`}
            </Button>
          )}
          <ConfirmDialog
            open={showRandomConfirm}
            title="Draft a random team?"
            description={`We'll fill your remaining ${totalSlots - slotsUsed} slot${totalSlots - slotsUsed === 1 ? '' : 's'} with a smart-random mix that spends most of your ${formatFranc(budgetLeft)} budget. You can still swap anyone before finalizing.`}
            confirmLabel="Draft Random"
            onConfirm={async () => {
              setShowRandomConfirm(false);
              setRandomBusy(true);
              try {
                const res = await teamAPI.draftRandom({ team_id: team?.id || teamId, league_id: leagueId || team?.league_id });
                toast.success(res.data?.message || 'Random team drafted!');
              } catch (e) {
                const d = e.response?.data?.detail;
                // Self-heal: "already full" means our UI was stale — the
                // refetch below resyncs it, so inform instead of alarming.
                if (typeof d === 'string' && d.toLowerCase().includes('already full')) toast.info(d);
                else toast.error(typeof d === 'string' ? d : 'Random draft failed — please try again');
              } finally {
                // ALWAYS refetch — a timed-out request may still have landed
                // server-side; never leave the roster UI on a stale snapshot.
                try { await loadData(); } catch (e2) { /* best-effort */ }
                setRandomBusy(false);
              }
            }}
            onCancel={() => setShowRandomConfirm(false)}
          />
          <DraftingOverlay visible={randomBusy} />
        </div>

        {/* Category filters — right-edge fade (iter_188) signals the row
            scrolls on narrow phones instead of looking clipped. */}
        <div className="relative">
          <div className="px-4 pb-2 flex gap-1 overflow-x-auto">
            {visibleTypes.map(t => (
              <button
                key={t.value}
                onClick={() => setCategory(t.value)}
                className={`shrink-0 px-2.5 py-1 text-[10px] rounded-full border transition-colors ${
                  category === t.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-border'
                }`}
                data-testid={`draft-cat-filter-${t.value || 'all'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background/90 to-transparent sm:hidden" />
        </div>
      </div>

      <div className="flex" style={{ height: 'calc(100vh - 180px)' }}>
        {/* Entity List */}
        <div className="flex-1 p-4 overflow-hidden">
          <StarterPicksRow
            visible={!loading && slotsUsed < 3}
            onAdd={handleAddClick}
            isOnTeam={isOnTeam}
            budgetLeft={budgetLeft}
            isDraftable={(e) =>
              allowedCategories.includes(e.entity_type) &&
              (categoryCounts[e.entity_type] || 0) < getMaxForCategory(e.entity_type)}
          />
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="draft-search" />
            </div>
          </div>

          {/* Sort ovals — parity with Explore (no Trending / category strip here). */}
          <div className="flex gap-1.5 mb-3 overflow-x-auto" data-testid="draft-sort-row">
            {[
              { key: 'pricing.price', label: 'Price' },
              { key: 'stats.weekly_points', label: 'Recent Form' },
              { key: 'stats.ownership_percentage', label: 'Owned' },
              { key: 'name', label: 'A-Z', dir: 1 },
              { key: 'stats.total_points', label: '\u20A3 Earned' },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => {
                  if (sortBy === opt.key) { setSortDir((d) => -d); }
                  else { setSortBy(opt.key); setSortDir(opt.dir || -1); }
                }}
                className={`shrink-0 px-2.5 py-1 text-[10px] rounded-full border transition-colors ${
                  sortBy === opt.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-border'
                }`}
                data-testid={`draft-sort-${opt.key}`}
              >
                {opt.label} {sortBy === opt.key && (sortDir === -1 ? '\u2193' : '\u2191')}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-2 h-[calc(100%-50px)] overflow-hidden">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : (
            /* 2026-07-20 launch perf — virtualized list (react-virtuoso) */
            <Virtuoso
              style={{ height: 'calc(100% - 50px)' }}
              data={entities}
              computeItemKey={(_, entity) => entity.id}
              increaseViewportBy={{ top: 200, bottom: 400 }}
              components={DRAFT_VIRTUOSO_COMPONENTS}
              context={{ loadMore, loadingMore, hasMore, shown: entities.length }}
              itemContent={(_, entity) => {
                  const onTeam = isOnTeam(entity.id);
                  const cantAfford = entity.pricing?.price > budgetLeft;
                  const catFull = categoryCounts[entity.entity_type] >= getMaxForCategory(entity.entity_type);
                  const notAllowed = !allowedCategories.includes(entity.entity_type);
                  const disabled = onTeam || cantAfford || catFull || notAllowed;
                  return (
                    <div className="pb-1.5">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border border-border/60 bg-card ${disabled ? 'opacity-50' : 'hover:shadow-sm'}`}>
                      {/* v5.7.63 — clickable region (avatar + name + meta)
                          deep-links to the person's profile. The Draft/Drop
                          button on the right has its own onClick and is
                          intentionally NOT wrapped in this region so a
                          tap on the button still drafts/drops the person. */}
                      <div
                        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => onEntityClick?.(entity.id)}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && onEntityClick) { e.preventDefault(); onEntityClick(entity.id); } }}
                        data-testid={`draft-row-open-${entity.id}`}
                      >
                        <EntityAvatar entity={entity} size={32} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{entity.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {/* v5.7.59 — colored singular oval matches the
                                ExploreTab so Draft Room and Search show the
                                same per-row labels (Ruler / Artist / etc.). */}
                            <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${ENTITY_TYPE_COLORS[entity.entity_type] || ''}`}>
                              {ENTITY_TYPE_SINGULAR[entity.entity_type] || entity.entity_type?.replace(/_/g, ' ')}
                            </Badge>
                            {entity.sub_type && entity.sub_type !== 'general' && (
                              <span className="text-[9px] text-muted-foreground">{entity.sub_type?.replace(/_/g, ' ')}</span>
                            )}
                            {entity.metadata?.country && (
                              <span className="text-[10px] text-muted-foreground">{entity.metadata.country}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs font-bold tabular-nums shrink-0">{formatFranc(entity.pricing?.price)}</span>
                      {onTeam ? (
                        // v5.7.57 — Drop button replaces the static "Added"
                        // badge so the user can remove from the same list
                        // without needing a separate right-side roster panel.
                        // v5.7.99 — Routed through handleRemoveClick which
                        // shows ConfirmDialog (always — drops are always
                        // costly enough to deserve a confirmation).
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] px-2 shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveClick(entity.id, entity.display_name || entity.name)}
                          disabled={removingId === entity.id}
                          data-testid={`draft-drop-${entity.id}`}
                        >
                          {removingId === entity.id ? '...' : 'Drop'}
                        </Button>
                      ) : notAllowed ? (
                        <span className="text-[8px] text-muted-foreground shrink-0">N/A</span>
                      ) : cantAfford ? (
                        <span className="text-[8px] text-destructive shrink-0">No ₣</span>
                      ) : catFull ? (
                        <span className="text-[8px] text-muted-foreground shrink-0">Full</span>
                      ) : (
                        // v5.7.99 — Routed through handleAddClick which
                        // confirms only when `team.finalized` is true.
                        <Button size="sm" className="h-6 text-[10px] px-2 shrink-0" onClick={() => handleAddClick(entity)} disabled={draftingId === entity.id || disabled} data-testid={`draft-add-${entity.id}`}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    </div>
                  );
                }}
            />
          )}
        </div>
        {/* v5.7.57 — Right-side "Your Roster" column removed. Draft/Drop
            buttons inline in the main list above are the single source
            of truth for managing the roster. */}
      </div>

      {/* v5.7.99 — Post-finalization Add confirmation. 2026-09-03: fee now
          surfaced by <FeeWarning/> ("This move will cost ₣X — Y% of your cash"). */}
      <ConfirmDialog
        open={!!confirmAdd}
        onCancel={() => setConfirmAdd(null)}
        title="Add to your finalized team?"
        description={(() => {
          if (!confirmAdd) return '';
          const priceStr = confirmAdd.price != null ? formatFranc(confirmAdd.price) : '₣?';
          // 2026-10-12 — open-at-Finalize slots fill for the price only.
          if (freeFillsLeft(team) > 0) {
            return `Add ${confirmAdd.name} to your team for ${priceStr}? This fills one of your open slots — no roster-move fee and it doesn't count toward this week's moves.`;
          }
          const free = nextPenaltyRate(team?.weekly_transfers_used || 0) === 0;
          return `Add ${confirmAdd.name} to your team for ${priceStr}? This counts as a roster move${
            free ? ' — your 1st this week, so it\'s FREE. Subsequent moves this week incur an escalating budget fee.' : '.'
          }`;
        })()}
        extra={confirmAdd ? <FeeWarning team={team} /> : null}
        confirmLabel="Add to team"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (!confirmAdd) return;
          const { entityId, name } = confirmAdd;
          setConfirmAdd(null);
          handleDraft(entityId, name);
        }}
      />

      {/* v5.7.99 — Drop confirmation (always — costly action). 2026-09-03:
          drops are FREE (no fee, no move counted); only adds carry the ladder. */}
      <ConfirmDialog
        open={!!confirmDrop}
        onCancel={() => setConfirmDrop(null)}
        title="Drop from your team?"
        description={
          confirmDrop
            ? team?.finalized
              ? `Drop ${confirmDrop.name}? Dropping is free — you'll be refunded their current market price and it won't count as a roster move. Only adding a replacement counts toward this week's move fees.`
              : `Drop ${confirmDrop.name}? You'll be refunded exactly what you paid (no penalty during the draft phase).`
            : ''
        }
        extra={confirmDrop && team?.id ? <DropPreview teamId={team.id} entityId={confirmDrop.entityId} /> : null}
        confirmLabel="Drop"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={() => {
          if (!confirmDrop) return;
          const { entityId, name } = confirmDrop;
          setConfirmDrop(null);
          handleRemove(entityId, name);
        }}
      />
    </div>
  );
}
